// client/src/components/PlaylistPlayer.tsx

import React, { useState, useRef, useEffect } from 'react';
// ✅ Import new icons
import { Play, Pause, SkipBack, SkipForward, Loader, AlertCircle, Volume2, VolumeX } from 'lucide-react';
import { useAudioPlayer } from '../context/AudioPlayerContext';

// ✅ Add custom CSS for volume slider
const volumeSliderCSS = `
  .volume-slider::-webkit-slider-thumb {
    appearance: none;
    height: 16px;
    width: 16px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    border: 2px solid #ffffff;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  }
  .volume-slider::-moz-range-thumb {
    height: 16px;
    width: 16px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    border: 2px solid #ffffff;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  }
`;

interface PlaylistItem {
  id: string;
  title: string;
  type: 'audio' | 'video';
}

interface PlaylistPlayerProps {
  items: PlaylistItem[];
  playlistId: string;
}

const PlaylistPlayer: React.FC<PlaylistPlayerProps> = ({ items, playlistId }) => {
  const { currentlyPlaying, setCurrentlyPlaying } = useAudioPlayer();
  
  const audioRef = useRef<HTMLAudioElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [played, setPlayed] = useState(0);
  const [seeking, setSeeking] = useState(false);

  // ✅ STEP 1: Add state for loading and error handling
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // ✅ Add volume control state
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);

  const currentItem = items[currentTrackIndex];
  const isVideo = currentItem?.type === 'video';
  
  const API_BASE_URL = import.meta.env.VITE_APP_SERVER;
  const mediaUrl = currentItem ? `${API_BASE_URL}/api/media/url/${currentItem.id}` : '';

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (document.getElementById('volume-slider-style')) return;
    const style = document.createElement('style');
    style.id = 'volume-slider-style';
    style.innerHTML = volumeSliderCSS;
    document.head.appendChild(style);
  }, []);

  useEffect(() => {
    setIsPlaying(currentlyPlaying === playlistId);
  }, [currentlyPlaying, playlistId]);

  useEffect(() => {
    const mediaElement = isVideo ? videoRef.current : audioRef.current;
    if (mediaElement) {
      if (isPlaying) {
        mediaElement.play().catch(e => console.error("Playback Error:", e));
      } else {
        mediaElement.pause();
      }
    }
  }, [isPlaying, currentTrackIndex, isVideo]);
  
  // ✅ Reset state when the track changes
  useEffect(() => {
    setIsLoading(false);
    setError(null);
  }, [currentTrackIndex]);

  const handlePlayPause = () => {
    if (error) setError(null); // Clear error on play attempt
    setCurrentlyPlaying(isPlaying ? null : playlistId);
  };

  const handleNext = () => {
    setCurrentTrackIndex((prev) => (prev + 1) % items.length);
  };

  const handlePrevious = () => {
    setCurrentTrackIndex((prev) => (prev - 1 + items.length) % items.length);
  };

  const selectTrack = (index: number) => {
    setCurrentTrackIndex(index);
    if (!isPlaying) {
      setCurrentlyPlaying(playlistId);
    }
  };
  
  const handleSeekMouseUp = () => {
    setSeeking(false);
    const mediaElement = isVideo ? videoRef.current : audioRef.current;
    if (mediaElement) {
      mediaElement.currentTime = duration * played;
    }
  };
  
  const formatTime = (seconds: number) => {
    if (isNaN(seconds) || !isFinite(seconds)) return "0:00";
    const date = new Date(seconds * 1000);
    return `${date.getUTCMinutes()}:${date.getUTCSeconds().toString().padStart(2, '0')}`;
  };

  if (!currentItem) return null;

  return (
    <div className="playlist-player my-4 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg shadow-md">
      <audio
        ref={audioRef}
        src={!isVideo ? mediaUrl : ''}
        preload="metadata"
        onLoadedData={() => !isVideo && audioRef.current && setDuration(audioRef.current.duration)}
        onTimeUpdate={(e) => !seeking && !isVideo && setPlayed(e.currentTarget.currentTime / e.currentTarget.duration || 0)}
        onEnded={handleNext}
        hidden
        // ✅ STEP 2: Add event handlers to the audio element
        onWaiting={() => setIsLoading(true)}
        onPlaying={() => setIsLoading(false)}
        onError={() => {
          setIsLoading(false);
          setError("Can't load track.");
          console.error("Playlist audio error:", audioRef.current?.error);
        }}
        // ✅ Add volume control to audio element
        volume={volume}
        muted={muted}
      />
      
      {isVideo && (
        <video
          ref={videoRef}
          src={isVideo ? mediaUrl : ''}
          className="w-full rounded-lg bg-black mb-2"
          controls
          onPlay={() => setCurrentlyPlaying(playlistId)}
          onPause={() => setIsPlaying(false)}
          onEnded={handleNext}
          preload="metadata"
        />
      )}
      
      {!isVideo && (
        // ✅ STEP 3: Wrap controls in a relative container for the overlay
        <div className="relative">
          {(isLoading || error) && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100/80 dark:bg-gray-800/80 rounded-lg z-10 -m-4">
              {isLoading && <Loader className="w-8 h-8 animate-spin text-blue-500" />}
              {error && (
                <div className="flex items-center text-red-500">
                  <AlertCircle className="w-5 h-5 mr-2" />
                  <span className="text-sm font-medium">{error}</span>
                </div>
              )}
            </div>
          )}

          <div className="mb-3">
            <p className="font-bold text-lg text-gray-800 dark:text-gray-100 truncate">{currentItem.title}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Track {currentTrackIndex + 1} of {items.length}</p>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button onClick={handlePrevious} title="Previous Track"><SkipBack size={20} /></button>
              <button onClick={handlePlayPause} className="p-3 rounded-full bg-blue-500 text-white">{isPlaying ? <Pause size={24} /> : <Play size={24} />}</button>
              <button onClick={handleNext} title="Next Track"><SkipForward size={20} /></button>
            </div>
            
            {/* ✅ Volume control moved to right side */}
            <div className="flex items-center space-x-2">
              <button 
                onClick={() => {
                  const newMuted = !muted;
                  setMuted(newMuted);
                  // ✅ Apply mute change to audio element immediately
                  if (audioRef.current) {
                    audioRef.current.muted = newMuted;
                  }
                }} 
                className="p-1 text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white transition-colors"
                title={muted ? "Unmute" : "Mute"}
              >
                {muted || volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </button>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={muted ? 0 : volume}
                onChange={(e) => {
                  const newVolume = parseFloat(e.target.value);
                  setVolume(newVolume);
                  if (newVolume === 0) {
                    setMuted(true);
                  } else if (muted) {
                    setMuted(false);
                  }
                  // ✅ Apply volume change to audio element immediately
                  if (audioRef.current) {
                    audioRef.current.volume = newVolume;
                    audioRef.current.muted = newVolume === 0;
                  }
                }}
                className="w-20 h-1 bg-gray-300 rounded-full appearance-none cursor-pointer dark:bg-gray-500 volume-slider"
                title={`Volume: ${Math.round((muted ? 0 : volume) * 100)}%`}
                style={{
                  background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(muted ? 0 : volume) * 100}%, #d1d5db ${(muted ? 0 : volume) * 100}%, #d1d5db 100%)`
                }}
              />
              <span className="text-xs text-gray-500 dark:text-gray-400 w-8">
                {Math.round((muted ? 0 : volume) * 100)}%
              </span>
            </div>
          </div>
          <div className="flex items-center space-x-2 mt-3">
            <span>{formatTime(played * duration)}</span>
            <input
              type="range" min={0} max={0.999999} step="any"
              value={played}
              onMouseDown={() => setSeeking(true)}
              onChange={(e) => setPlayed(parseFloat(e.target.value))}
              onMouseUp={handleSeekMouseUp}
              className="w-full"
            />
            <span>{formatTime(duration)}</span>
          </div>
        </div>
      )}

      <div className="mt-4 max-h-48 overflow-y-auto border-t border-gray-200 dark:border-gray-700 pt-2">
        {items.map((item, index) => (
          <div
            key={item.id}
            onClick={() => selectTrack(index)}
            className={`p-2 rounded-md cursor-pointer text-sm ${index === currentTrackIndex ? 'bg-blue-100 dark:bg-blue-900 font-semibold' : 'hover:bg-gray-200 dark:hover:bg-gray-700'}`}
          >
            {index + 1}. {item.title}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlaylistPlayer;
